import React from "react";
import { Formik, Form } from "formik";
import {
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  Autocomplete,
} from "@mui/material";
import axios from "../../api";
import SoftTypography from "components/SoftTypography";
import * as Yup from "yup";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  region_id: Yup.string().required("Region is required"),
  address_type: Yup.string().required("Address type is required"),
  address: Yup.string().required("Address is required"),
});
interface State {
  state_id: number;
  state_name: string;
}
interface City {
  city_id: number;
  city_name: string;
}
interface Region {
  region_id: number;
  region_name: string;
}
interface User {
  type: string;
}
interface AddAddressProps {
  states: State[] | null;
  cities: City[] | null;
  areas: Region[] | null;
  setSelectedCity: React.Dispatch<React.SetStateAction<number | null>>;
  setSelectedState: React.Dispatch<React.SetStateAction<number | null>>;
  fetchError: (msg: string) => void;
  fetchSuccess: (msg: string) => void;
  fetchAddressData: () => Promise<void>;
  onClose: () => void;
  userId: number | null;
  user: User | null;
}

interface Division {
  division_id: number;
  division_name: string;
}
const AddAddress = ({
  states,
  cities,
  areas,
  setSelectedCity,
  setSelectedState,
  fetchSuccess,
  fetchError,
  fetchAddressData,
  onClose,
  userId,
  user,
}: AddAddressProps) => {
  const [divisions, setDivisions] = React.useState<Division[]>([]);
  React.useEffect(() => {
    if (userId) {
      axios
        .get(`/divisions/customer/${userId}`)
        .then((res) => {
          setDivisions(res.data.divisions || []);
        })
        .catch(() => setDivisions([]));
    }
  }, [userId]);
  return (
    <Formik
      initialValues={{
        state_id: "",
        city_id: "",
        region_id: "",
        address_type: "",
        address: "",
        division_id: user?.type === "company" ? "" : null,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        try {
          const payload = {
            ...values,
            customer_id: userId,
            division_id:
              user?.type === "company" ? values.division_id || null : null,
          };

          const response = await axios.post("/address", payload);
          fetchSuccess(response.data.message);
          fetchAddressData();
          onClose();
        } catch (error) {
          if (error instanceof AxiosError && error.response) {
            fetchError(error.response.data.error);
          }
        }
      }}
    >
      {({ values, handleChange, setFieldValue, errors, touched }) => (
        <Form>
          <Grid container spacing={2}>
            {user?.type === "company" && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Division
                  </SoftTypography>
                  <Autocomplete
                    options={divisions || []}
                    getOptionLabel={(option) => option.division_name || ""}
                    value={
                      divisions.find(
                        (d) => d.division_id === Number(values.division_id)
                      ) || null
                    }
                    onChange={(_, newValue) =>
                      setFieldValue("division_id", newValue?.division_id || "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="division_id"
                        variant="outlined"
                        error={!!errors.division_id && touched.division_id}
                        helperText={touched.division_id && errors.division_id}
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold">
                  State
                </SoftTypography>
                <Autocomplete
                  id="state-autocomplete"
                  options={states || []}
                  getOptionLabel={(option) => option.state_name || ""}
                  value={
                    (states as unknown as any[])?.find(
                      (state: { state_id: string }) =>
                        state.state_id === values.state_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFieldValue("city_id", "");
                    setFieldValue("region_id", "");
                    setFieldValue(
                      "state_id",
                      newValue ? newValue.state_id : ""
                    );
                    setSelectedState(
                      newValue ? Number(newValue.state_id) : null
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="state_id"
                      variant="outlined"
                      error={!!errors.state_id && touched.state_id}
                      helperText={touched.state_id && errors.state_id}
                      fullWidth
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold">
                  City
                </SoftTypography>
                <Autocomplete
                  id="city-autocomplete"
                  options={cities || []}
                  getOptionLabel={(option) => option.city_name || ""}
                  value={
                    (cities as unknown as any[])?.find(
                      (city) => city.city_id === values.city_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFieldValue("region_id", "");
                    setFieldValue("city_id", newValue ? newValue.city_id : "");
                    setSelectedCity(newValue ? Number(newValue.city_id) : null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="city_id"
                      variant="outlined"
                      error={!!errors.city_id && touched.city_id}
                      helperText={touched.city_id && errors.city_id}
                      fullWidth
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold">
                  Area
                </SoftTypography>
                <Autocomplete
                  id="region-autocomplete"
                  options={areas || []}
                  getOptionLabel={(option) => option.region_name || ""}
                  value={
                    (areas as unknown as any[])?.find(
                      (city) => city.region_id === values.region_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFieldValue(
                      "region_id",
                      newValue ? newValue.region_id : ""
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="region_id"
                      variant="outlined"
                      error={!!errors.region_id && touched.region_id}
                      helperText={touched.region_id && errors.region_id}
                      fullWidth
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Address Type"
                name="address_type"
                value={values.address_type}
                onChange={handleChange}
                error={!!errors.address_type && touched.address_type}
                helperText={touched.address_type && errors.address_type}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Address"
                name="address"
                value={values.address}
                onChange={handleChange}
                error={!!errors.address && touched.address}
                helperText={touched.address && errors.address}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Button
                  className="success-btn"
                  sx={{
                    position: "relative",
                    minWidth: 100,
                  }}
                  variant="contained"
                  type="submit"
                  color="success"
                >
                  Save
                </Button>

                <Button
                  className="cancel-btn"
                  sx={{
                    position: "relative",
                    minWidth: 100,
                    ml: 2.5,
                  }}
                  variant="contained"
                  color="warning"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default AddAddress;
